import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PRODUCTS, CATEGORIES, loadCart, saveCart } from './productData';
import ProductCard from './ProductCard';
import CartDrawer from './CartDrawer';
import ProductModal from './ProductModal';
import './Ecommerce.css';

function Ecommerce() {
  const navigate = useNavigate();
  const [cart, setCart] = useState(loadCart);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [category, setCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceRange, setPriceRange] = useState([0, 300]);
  const [showFilters, setShowFilters] = useState(false);

  const updateCart = (newCart) => {
    setCart(newCart);
    saveCart(newCart);
  };

  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      updateCart(cart.map((item) =>
        item.id === product.id ? { ...item, qty: item.qty + 1 } : item
      ));
    } else {
      updateCart([...cart, { ...product, qty: 1 }]);
    }
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) {
      updateCart(cart.filter((item) => item.id !== productId));
    } else {
      updateCart(cart.map((item) =>
        item.id === productId ? { ...item, qty } : item
      ));
    }
  };

  const clearCart = () => updateCart([]);

  const filteredProducts = useMemo(() => {
    let result = PRODUCTS.filter((p) => {
      if (category !== 'All' && p.category !== category) return false;
      if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (p.price < priceRange[0] || p.price > priceRange[1]) return false;
      return true;
    });

    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'rating': result.sort((a, b) => b.rating - a.rating); break;
      case 'reviews': result.sort((a, b) => b.reviews - a.reviews); break;
      default: break;
    }
    return result;
  }, [category, sortBy, searchQuery, priceRange]);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  return (
    <div className="ecom-app">
      <div className="ecom-topbar">
        <div className="ecom-topbar-left">
          <button className="ecom-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ecom-title">Store</h1>
        </div>
        <div className="ecom-topbar-center">
          <div className="ecom-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="ecom-topbar-right">
          <button className="ecom-filter-toggle" onClick={() => setShowFilters(!showFilters)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            Filters
          </button>
          <button className="ecom-cart-btn" onClick={() => setShowCart(true)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
            </svg>
            {cartCount > 0 && <span className="ecom-cart-badge">{cartCount}</span>}
          </button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className={`ecom-filters ${showFilters ? 'show' : ''}`}>
        <div className="ecom-categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`ecom-cat-btn ${category === cat ? 'active' : ''}`}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="ecom-filter-controls">
          <div className="ecom-price-filter">
            <label>Price: ${priceRange[0]} - ${priceRange[1]}</label>
            <input
              type="range"
              min="0"
              max="300"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
            />
          </div>
          <select className="ecom-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="reviews">Most Reviews</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="ecom-results-info">
        <span>{filteredProducts.length} products found</span>
      </div>

      {/* Products Grid */}
      <div className="ecom-products">
        {filteredProducts.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onAddToCart={() => addToCart(product)}
            onClick={() => setSelectedProduct(product)}
            inCart={cart.some((item) => item.id === product.id)}
          />
        ))}
        {filteredProducts.length === 0 && (
          <div className="ecom-empty">No products found matching your criteria.</div>
        )}
      </div>

      {/* Cart Drawer */}
      {showCart && (
        <CartDrawer
          cart={cart}
          total={cartTotal}
          onUpdateQty={updateQty}
          onClear={clearCart}
          onClose={() => setShowCart(false)}
        />
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={() => { addToCart(selectedProduct); setSelectedProduct(null); }}
          inCart={cart.some((item) => item.id === selectedProduct.id)}
        />
      )}
    </div>
  );
}

export default Ecommerce;
